import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, TrendingUp, AlertTriangle, Clock, GitBranch, 
  ChevronRight, Shield, DollarSign, Target, Map
} from 'lucide-react';
import { api } from '../api/client';
import HierarchicalRoadmap from '../components/HierarchicalRoadmap';
import { useFilters } from '../context/FilterContext';
import { useTenant } from '../context/TenantContext';
import { GovernanceSummaryWidget } from '../components/GovernanceWidget';
import { ExecutiveMilestonesWidget } from '../components/MilestonesWidget';
import ScopeHealthWidget from '../components/ScopeHealthWidget';
import { PageErrorBoundary, WidgetErrorBoundary } from '../components/ErrorBoundary';
import { SkeletonKPIGrid, SkeletonWidget, SkeletonPage } from '../components/Skeleton';
import { NoDataState } from '../components/EmptyState';
import { toNumber, clamp } from '../utils/dataAdapters';
import { useCurrency } from '../utils/currencyUtils';

const ExecutiveDashboard = () => {
  const navigate = useNavigate();
  const { getFilterParams } = useFilters();
  const { getCurrency } = useTenant();
  const { formatCurrency, currencySymbol } = useCurrency();
  const [data, setData] = useState(null);
  const [roadmapData, setRoadmapData] = useState(null);
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roadmapScale, setRoadmapScale] = useState('quarter');
  const [showReleaseBands, setShowReleaseBands] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading Executive Dashboard data...');
      
      // Load dashboard data first (fast)
      const dashboardRes = await api.getExecutiveDashboard();
      setData(dashboardRes.data);
      
      // Load roadmap in parallel (can be slow with large datasets)
      const [roadmapRes, releasesRes] = await Promise.all([
        api.getPortfolioRoadmap(null, 1), // Only show portfolio level for performance
        api.getReleases()
      ]);
      
      setRoadmapData(roadmapRes.data?.roadmap || []);
      setReleases(releasesRes.data?.releases || []);
    } catch (err) {
      console.error('Failed to load executive dashboard:', err);
      setError(err);
      // Set empty data to show the "no data" screen instead of infinite loading
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const getHealthColor = (health) => {
    const colors = { 
      red: 'bg-[var(--clr-sev-blocked)]', 
      yellow: 'bg-[var(--clr-sev-atrisk)]', 
      green: 'bg-[var(--clr-sev-healthy)]' 
    };
    return colors[health] || 'bg-[var(--clr-sev-notstarted)]';
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <SkeletonKPIGrid count={5} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SkeletonWidget height="300px" />
          <SkeletonWidget height="300px" />
        </div>
        <SkeletonWidget height="250px" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertTriangle className="w-12 h-12 text-red-400 mb-4" />
        <p className="text-gray-600 font-medium mb-2">Failed to load dashboard</p>
        <p className="text-sm text-gray-500 mb-4">{error.message || 'Please try again'}</p>
        <button onClick={loadData} className="px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg">
          Retry
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <NoDataState 
        title="No portfolio data available"
        description="Portfolio information will appear here once you have projects set up."
      />
    );
  }

  return (
    <div className="space-y-6 bg-[var(--clr-surface-page)] min-h-screen p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-3">
            <Building2 className="w-7 h-7 text-[var(--clr-brand-primary)]" />
            Executive Dashboard
          </h1>
          <p className="text-[var(--clr-text-secondary)] mt-1">Company-wide visibility and strategic insights</p>
        </div>
      </div>

      {/* Scope Health Widget - Top of dashboard */}
      <ScopeHealthWidget />

      {/* KPI Tiles - Modern Color System */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-[var(--clr-surface-card)] rounded-lg border-l-4 p-4 shadow-[var(--shadow-card)]" style={{borderLeftColor: 'var(--clr-sev-inprogress)'}} data-testid="kpi-percent-complete">
          <div className="flex items-center justify-between mb-2">
            <Target className="w-5 h-5 text-[var(--clr-sev-inprogress)]" />
            <span className="text-xs text-[var(--clr-text-muted)]">Weighted</span>
          </div>
          <p className="text-3xl font-bold text-[var(--clr-text-primary)]">{data.kpis.percent_complete}%</p>
          <p className="text-sm text-[var(--clr-text-secondary)]">Complete</p>
        </div>

        <div className="bg-[var(--clr-surface-card)] rounded-lg border-l-4 p-4 shadow-[var(--shadow-card)]" style={{borderLeftColor: 'var(--clr-sev-blocked)'}} data-testid="kpi-blocked">
          <div className="flex items-center justify-between mb-2">
            <div className="w-3 h-3 rounded-full bg-[var(--clr-sev-blocked)]"></div>
          </div>
          <p className="text-3xl font-bold text-[var(--clr-sev-blocked)]">{data.kpis.blocked_count}</p>
          <p className="text-sm text-[var(--clr-text-secondary)]">Blocked</p>
        </div>

        <div className="bg-[var(--clr-surface-card)] rounded-lg border-l-4 p-4 shadow-[var(--shadow-card)]" style={{borderLeftColor: 'var(--clr-sev-behind)'}} data-testid="kpi-behind">
          <div className="flex items-center justify-between mb-2">
            <div className="w-3 h-3 rounded-full bg-[var(--clr-sev-behind)]"></div>
          </div>
          <p className="text-3xl font-bold text-[var(--clr-sev-behind)]">{data.kpis.behind_count}</p>
          <p className="text-sm text-[var(--clr-text-secondary)]">Behind</p>
        </div>

        <div className="bg-[var(--clr-surface-card)] rounded-lg border-l-4 p-4 shadow-[var(--shadow-card)]" style={{borderLeftColor: 'var(--clr-sev-atrisk)'}} data-testid="kpi-at-risk">
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle className="w-5 h-5 text-[var(--clr-sev-atrisk)]" />
          </div>
          <p className="text-3xl font-bold text-[var(--clr-sev-atrisk)]">{data.kpis.at_risk_count}</p>
          <p className="text-sm text-[var(--clr-text-secondary)]">At Risk</p>
        </div>

        <div className="bg-[var(--clr-surface-card)] rounded-lg border-l-4 p-4 shadow-[var(--shadow-card)]" style={{borderLeftColor: 'var(--clr-sev-notstarted)'}} data-testid="kpi-variance">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-5 h-5 text-[var(--clr-text-muted)]" />
          </div>
          <p className={`text-3xl font-bold ${data.kpis.cost_variance > 0 ? 'text-[var(--clr-sev-blocked)]' : 'text-[var(--clr-sev-healthy)]'}`}>
            {data.kpis.cost_variance > 0 ? '+' : ''}{formatCurrency(data.kpis.cost_variance)}
          </p>
          <p className="text-sm text-[var(--clr-text-secondary)]">Cost Variance</p>
        </div>

        <div className="bg-[var(--clr-surface-card)] rounded-lg border-l-4 p-4 shadow-[var(--shadow-card)]" style={{borderLeftColor: 'var(--clr-sev-healthy)'}} data-testid="kpi-confidence">
          <div className="flex items-center justify-between mb-2">
            <Shield className="w-5 h-5 text-[var(--clr-sev-healthy)]" />
          </div>
          <p className={`text-3xl font-bold ${
            data.kpis.delivery_confidence >= 70 ? 'text-[var(--clr-sev-healthy)]' : 
            data.kpis.delivery_confidence >= 40 ? 'text-[var(--clr-sev-atrisk)]' : 'text-[var(--clr-sev-blocked)]'
          }`}>
            {data.kpis.delivery_confidence}%
          </p>
          <p className="text-sm text-[var(--clr-text-secondary)]">Delivery Confidence</p>
        </div>
      </div>

      {/* Governance Summary Widget */}
      <GovernanceSummaryWidget className="mb-6" />

      {/* Strategic Milestones Widget */}
      <ExecutiveMilestonesWidget className="mb-6" />

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top 5 Risks */}
        <div className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] shadow-[var(--shadow-card)]">
          <div className="p-4 border-b border-[var(--clr-border-light)] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[var(--clr-text-primary)] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[var(--clr-sev-atrisk)]" />
              Top 5 Risks Requiring Executive Attention
            </h2>
            <button 
              onClick={() => navigate('/issues?escalation_level=Executive%2FSteerCo')}
              className="text-sm text-[var(--clr-text-link)] hover:underline"
            >
              View All
            </button>
          </div>
          <div className="divide-y divide-[var(--clr-border-light)]">
            {data.top_risks?.length > 0 ? data.top_risks.map((risk, idx) => (
              <div 
                key={risk.id}
                className="p-4 hover:bg-[var(--clr-surface-hover)] cursor-pointer flex items-center gap-4"
                onClick={() => navigate(`/issues?id=${risk.id}`)}
                data-testid={`top-risk-${idx}`}
              >
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[var(--clr-sev-blocked)] bg-opacity-10 flex items-center justify-center text-[var(--clr-sev-blocked)] font-bold">
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[var(--clr-text-primary)] truncate">{risk.title}</p>
                  <div className="flex items-center gap-2 text-sm text-[var(--clr-text-muted)]">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium text-white ${
                      risk.escalation_level === 'Executive/SteerCo' ? 'bg-[var(--clr-sev-blocked)]' :
                      risk.escalation_level === 'Portfolio' ? 'bg-[var(--clr-sev-atrisk)]' :
                      risk.escalation_level === 'Program' ? 'bg-[var(--clr-sev-atrisk)]' :
                      'bg-[var(--clr-sev-notstarted)]'
                    }`}>
                      {risk.escalation_level}
                    </span>
                    <span>{risk.priority}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-[var(--clr-sev-blocked)]">{risk.risk_score?.toFixed(1)}</p>
                  <p className="text-xs text-[var(--clr-text-muted)]">Risk Score</p>
                </div>
              </div>
            )) : (
              <div className="p-8 text-center text-[var(--clr-text-muted)]">
                <Shield className="w-12 h-12 text-[var(--clr-text-muted)] opacity-30 mx-auto mb-2" />
                <p>No high-priority risks</p>
              </div>
            )}
          </div>
        </div>

        {/* Top 5 Blockers */}
        <div className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] shadow-[var(--shadow-card)]">
          <div className="p-4 border-b border-[var(--clr-border-light)] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[var(--clr-text-primary)] flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-[var(--clr-sev-inprogress)]" />
              Top 5 Blocking Dependencies
            </h2>
            <button 
              onClick={() => navigate('/dependencies')}
              className="text-sm text-[var(--clr-text-link)] hover:underline"
            >
              View All
            </button>
          </div>
          <div className="divide-y divide-[var(--clr-border-light)]">
            {data.top_blockers?.length > 0 ? data.top_blockers.map((blocker, idx) => (
              <div 
                key={blocker.id}
                className="p-4 hover:bg-[var(--clr-surface-hover)] cursor-pointer"
                onClick={() => navigate('/dependencies')}
                data-testid={`top-blocker-${idx}`}
              >
                <div className="flex items-center gap-4">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[var(--clr-sev-blocked)] bg-opacity-10 flex items-center justify-center text-[var(--clr-sev-blocked)] font-bold">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[var(--clr-text-primary)] truncate">
                      {blocker.from_project} → {blocker.to_project}
                    </p>
                    <p className="text-sm text-[var(--clr-text-muted)] truncate">{blocker.description || 'No description'}</p>
                    {blocker.is_cross_program && (
                      <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium bg-[var(--clr-brand-primary)] text-white">
                        Cross-Program
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-[var(--clr-sev-atrisk)]">{blocker.aging}</p>
                    <p className="text-xs text-[var(--clr-text-muted)]">Days Aging</p>
                  </div>
                </div>
              </div>
            )) : (
              <div className="p-8 text-center text-[var(--clr-text-muted)]">
                <Clock className="w-12 h-12 text-[var(--clr-text-muted)] opacity-30 mx-auto mb-2" />
                <p>No blocking dependencies</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Portfolio Heatmap */}
      <div className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] shadow-[var(--shadow-card)]">
        <div className="p-4 border-b border-[var(--clr-border-light)] flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">Portfolio Heatmap</h2>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-[var(--clr-sev-blocked)]"></div>
              <span className="text-[var(--clr-text-secondary)]">Critical</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-[var(--clr-sev-atrisk)]"></div>
              <span className="text-[var(--clr-text-secondary)]">At Risk</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-[var(--clr-sev-healthy)]"></div>
              <span className="text-[var(--clr-text-secondary)]">On Track</span>
            </div>
          </div>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {data.portfolio_heatmap?.map((portfolio) => (
              <div
                key={portfolio.id}
                onClick={() => navigate(`/portfolio/${portfolio.id}`)}
                className="border border-[var(--clr-border-light)] rounded-lg p-4 hover:shadow-[var(--shadow-md)] cursor-pointer transition-all bg-[var(--clr-surface-card)]"
                data-testid={`portfolio-heatmap-${portfolio.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-[var(--clr-text-primary)]">{portfolio.name}</h3>
                  <div className={`w-4 h-4 rounded-full ${getHealthColor(portfolio.health)}`}></div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-[var(--clr-text-secondary)]">% Complete</span>
                    <span className="font-medium text-[var(--clr-text-primary)]">{portfolio.percent_complete}%</span>
                  </div>
                  <div className="w-full bg-[var(--clr-border-light)] rounded-full h-2">
                    <div 
                      className="h-2 rounded-full"
                      style={{ 
                        width: `${portfolio.percent_complete}%`,
                        backgroundColor: portfolio.color
                      }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-sm pt-2">
                    <div>
                      <span className="text-[var(--clr-sev-blocked)] font-medium">{portfolio.blocked_count}</span>
                      <span className="text-[var(--clr-text-muted)] ml-1">blocked</span>
                    </div>
                    <div>
                      <span className="text-[var(--clr-sev-behind)] font-medium">{portfolio.behind_count}</span>
                      <span className="text-[var(--clr-text-muted)] ml-1">behind</span>
                    </div>
                  </div>
                  <div className="text-right text-sm">
                    <span className={`font-medium ${portfolio.variance > 0 ? 'text-[var(--clr-sev-blocked)]' : 'text-[var(--clr-sev-healthy)]'}`}>
                      {portfolio.variance > 0 ? '+' : ''}{formatCurrency(portfolio.variance)}
                    </span>
                    <span className="text-[var(--clr-text-muted)] ml-1">variance</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Roadmap - Hierarchical View */}
      <div className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] shadow-[var(--shadow-card)]">
        <div className="p-4 border-b border-[var(--clr-border-light)] flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[var(--clr-text-primary)] flex items-center gap-2">
            <Map className="w-5 h-5 text-[var(--clr-brand-primary)]" />
            Portfolio Roadmap
          </h2>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={showReleaseBands}
                onChange={(e) => setShowReleaseBands(e.target.checked)}
                className="rounded text-[var(--clr-brand-primary)]"
              />
              <span className="text-[var(--clr-text-secondary)]">Release Bands</span>
            </label>
            <select
              value={roadmapScale}
              onChange={(e) => setRoadmapScale(e.target.value)}
              className="text-sm border border-[var(--clr-border-light)] rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-[var(--clr-brand-primary)] bg-[var(--clr-surface-card)]"
              data-testid="roadmap-scale-selector"
            >
              <option value="quarter">Quarter View</option>
              <option value="month">Month View</option>
              <option value="sprint">Sprint View</option>
            </select>
          </div>
        </div>
        <div className="p-4">
          <HierarchicalRoadmap 
            data={roadmapData} 
            scale={roadmapScale}
            showReleaseBands={showReleaseBands}
            releases={releases}
            currency={getCurrency()}
            maxDepth={3}
            onItemClick={(item) => {
              if (item.type === 'Portfolio') {
                navigate(`/portfolio/${item.id}`);
              } else if (item.type === 'Program') {
                navigate(`/program/${item.id}`);
              } else if (item.type === 'Project') {
                navigate(`/project/${item.id}`);
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

// Wrap with PageErrorBoundary
const ExecutiveDashboardWithErrorBoundary = () => (
  <PageErrorBoundary pageName="Executive Dashboard">
    <ExecutiveDashboard />
  </PageErrorBoundary>
);

export default ExecutiveDashboardWithErrorBoundary;
